import type { GeoJSONSource, Map as MaplibreMap } from "maplibre-gl";
import { JPBReferenceProperty, PriceMode, AreaUnit, getSoilTypeColor } from "./JPBState";
import { JPBManager } from "./JPBManager";
import { GenerateGeom } from "src/managers/GenerateGeom";
import { BsHouseDoor } from "react-icons/bs";

export const SOURCE_ID_POLY = 'jpb-reference-properties-poly';
export const SOURCE_ID = 'jpb-reference-properties';
export const SOURCE_ID_FARMLAND = 'jpb-farmland-poly';
export const SOURCE_ID_JORDBUNDSKORT = 'jpb-jordbundskort-poly';
export const SOURCE_ID_HIGHLIGHT = 'jpb-row-click-highlight';
export const LAYER_ID = 'jpb-reference-properties-layer';
export const LAYER_ID_BUILDING = 'jpb-reference-properties-building-layer';
export const LAYER_ID_POLY = 'jpb-reference-properties-layer-poly';
export const LAYER_ID_FARMLAND = 'jpb-farmland-layer';
export const LAYER_ID_JORDBUNDSKORT = 'jpb-jordbundskort-layer';
export const LAYER_ID_HIGHLIGHT = 'jpb-row-click-highlight-layer';
export const TEXT_LAYER_ID = 'jpb-reference-properties-text';
export const ORIGINAL_TEXT_LAYER_ID = 'jpb-reference-properties-text-original';

const JPB_ICON_CHECKED = 'jpb-house-checked';
const JPB_ICON_UNCHECKED = 'jpb-house-unchecked';
const JPB_ICON_LOCKED = 'jpb-house-locked';

async function ensureJPBBuildingIcons(map: MaplibreMap): Promise<void> {
   const iconConfigs = [
      { id: JPB_ICON_CHECKED, size: 26, iconScale: 1 },
      { id: JPB_ICON_UNCHECKED, size: 26, iconScale: 1 },
      { id: JPB_ICON_LOCKED, size: 26, iconScale: 1 },
   ];

   for (const config of iconConfigs) {
      if (map.hasImage(config.id)) {
         continue;
      }

      try {
         const dataUrl = await GenerateGeom.reactIconMarker(BsHouseDoor, {
            iconColor: '#ffffff',
            size: config.size,
            iconScale: config.iconScale || 1,
            strokeWidth: 1.1
         });

         const image = await map.loadImage(dataUrl);
         if (!map.hasImage(config.id)) {
            map.addImage(config.id, image.data, { pixelRatio: 2 });
         }
      } catch (error) {
         console.error(`Failed to create JPB building icon ${config.id}:`, error);
      }
   }
}

export interface JPBMapLayerHandlers {
   onFeatureClick: (documentID: string, ctrlKey: boolean) => void;
   onFeatureHover?: (documentID: string | null) => void;
}

/**
 * Creates GeoJSON for farmland polygons from properties
 */
function createFarmlandGeoJson(properties: JPBReferenceProperty[]) {
   return {
      type: "FeatureCollection" as const,
      features: properties
         .filter(prop => prop.overlapGeometry?.farmlandGeometry)
         .map(prop => ({
            id: prop.documentID,
            type: "Feature" as const,
            geometry: prop.overlapGeometry!.farmlandGeometry,
            properties: {
               documentID: prop.documentID,
               bfe_nr: prop.bfe_nr
            }
         }))
   };
}



export function createJordbundskortGeoJson(properties: JPBReferenceProperty[], checkedItems: Set<string>, lockedBuildingDocumentIds?: Set<string>) {
   const features: any[] = [];

   properties.forEach(prop => {
      const isChecked = checkedItems.has(prop.documentID);
      const isLocked = lockedBuildingDocumentIds?.has(prop.documentID) ?? false;
      const jordbundskortGeometry = prop.overlapGeometry?.jordbundskortGeometry;
      if (jordbundskortGeometry) {
         if (jordbundskortGeometry.type === "FeatureCollection" && jordbundskortGeometry.features) {
            jordbundskortGeometry.features.forEach((feature: any, index: number) => {
               const jordtype = feature.properties?.Jordtype ||
                  feature.properties?.jordtype ||
                  feature.properties?.JB_kode ||
                  null;
               const color = getSoilTypeColor(jordtype);
               features.push({
                  id: `${prop.documentID}-jordbundskort-${index}`,
                  type: "Feature" as const,
                  geometry: feature.geometry,
                  properties: {
                     documentID: prop.documentID,
                     bfe_nr: prop.bfe_nr,
                     jordtype: jordtype,
                     color: color,
                     checked: isChecked,
                     locked: isLocked,
                     ...feature.properties
                  }
               });
            });
         }
      }
   });

   return {
      type: "FeatureCollection" as const,
      features
   };
}


export function setupJPBMapLayers(
   map: MaplibreMap,
   properties: JPBReferenceProperty[],
   checkedItems: Set<string>,
   priceMode: PriceMode,
   areaUnit: AreaUnit,
   handlers: JPBMapLayerHandlers
): () => void {
   const geoJson = JPBManager.convertToGeoJSON(properties, checkedItems);
   const geoJsonPoly = JPBManager.convertToGeojsonGeometry(properties, checkedItems);
   const farmlandGeoJson = createFarmlandGeoJson(properties);
   const jordbundskortGeoJson = createJordbundskortGeoJson(properties, checkedItems, new Set());

   if (map.getSource(SOURCE_ID_POLY)) {
      const source = map.getSource(SOURCE_ID_POLY) as GeoJSONSource;
      if (source) source.setData(geoJsonPoly);
   } else {
      map.addSource(SOURCE_ID_POLY, {
         type: 'geojson',
         promoteId: 'documentID',
         data: geoJsonPoly
      });
   }

   if (map.getSource(SOURCE_ID_FARMLAND)) {
      const source = map.getSource(SOURCE_ID_FARMLAND) as GeoJSONSource;
      if (source) source.setData(farmlandGeoJson);
   } else {
      map.addSource(SOURCE_ID_FARMLAND, {
         type: 'geojson',
         promoteId: 'documentID',
         data: farmlandGeoJson
      });
   }

   if (map.getSource(SOURCE_ID_JORDBUNDSKORT)) {
      const source = map.getSource(SOURCE_ID_JORDBUNDSKORT) as GeoJSONSource;
      if (source) source.setData(jordbundskortGeoJson);
   } else {
      map.addSource(SOURCE_ID_JORDBUNDSKORT, {
         type: 'geojson',
         data: jordbundskortGeoJson
      });
   }

   if (map.getSource(SOURCE_ID)) {
      const source = map.getSource(SOURCE_ID) as GeoJSONSource;
      if (source) source.setData(geoJson);
   } else {
      map.addSource(SOURCE_ID, {
         type: 'geojson',
         promoteId: 'documentID',
         data: geoJson
      });
   }

   const clickHandler = (e: any) => {
      if (e.originalEvent.defaultPrevented) return;
      e.originalEvent.preventDefault();
      e.preventDefault();
      if (e.features && e.features.length > 0) {
         const feature = e.features[0];
         const documentID = feature.properties?.documentID;
         if (documentID) {
            const ctrlKey = !!(e.originalEvent?.ctrlKey || e.originalEvent?.metaKey);
            handlers.onFeatureClick(documentID, ctrlKey);
         }
      }
      return false;
   };

   let hoveredFeatureId: string | number | null = null;

   const updateBuildingIconSizeForHover = (featureId: string | number | null, isHover: boolean) => {
      if (!featureId) return;

      const source = map.getSource(SOURCE_ID) as GeoJSONSource;
      if (!source || source.type !== 'geojson') return;

      const data = (source as any)._data as any;
      if (!data || data.type !== 'FeatureCollection' || !Array.isArray(data.features)) return;

      const feature = data.features.find((f: any) => f.id === featureId || f.properties?.documentID === featureId);
      if (!feature || !feature.properties?.hasBuilding || !feature.properties?.buildingIconName) return;

      const newIconSize = isHover ? 1.308 : 1.0;
      const currentIconSize = feature.properties.buildingIconSize ?? 1.0;

      if (Math.abs(currentIconSize - newIconSize) > 0.001) {
         feature.properties.buildingIconSize = newIconSize;
         source.setData(data);
      }
   };

   const mouseMoveHandler = (e: any) => {
      map.getCanvas().style.cursor = 'pointer';
      let newHoveredFeatureId: string | number | null = null;

      if (e.features && e.features.length > 0) {
         const feature = e.features[0];
         newHoveredFeatureId = feature.id;
      }

      if (hoveredFeatureId !== newHoveredFeatureId) {
         if (hoveredFeatureId !== null && hoveredFeatureId !== undefined) {
            map.setFeatureState(
               { source: SOURCE_ID_POLY, id: hoveredFeatureId },
               { hover: false }
            );
            map.setFeatureState(
               { source: SOURCE_ID, id: hoveredFeatureId },
               { hover: false }
            );
            updateBuildingIconSizeForHover(hoveredFeatureId, false);
         }

         if (newHoveredFeatureId !== null && newHoveredFeatureId !== undefined) {
            map.setFeatureState(
               { source: SOURCE_ID, id: newHoveredFeatureId },
               { hover: true }
            );
            map.setFeatureState(
               { source: SOURCE_ID_POLY, id: newHoveredFeatureId },
               { hover: true }
            );
            updateBuildingIconSizeForHover(newHoveredFeatureId, true);
            if (handlers.onFeatureHover) {
               handlers.onFeatureHover(String(newHoveredFeatureId));
            }
         } else {
            if (handlers.onFeatureHover) {
               handlers.onFeatureHover(null);
            }
         }

         hoveredFeatureId = newHoveredFeatureId;
      }
   };

   const mouseLeaveHandler = () => {
      map.getCanvas().style.cursor = '';
      if (hoveredFeatureId !== null && hoveredFeatureId !== undefined) {
         map.setFeatureState(
            { source: SOURCE_ID, id: hoveredFeatureId },
            { hover: false }
         );
         map.setFeatureState(
            { source: SOURCE_ID_POLY, id: hoveredFeatureId },
            { hover: false }
         );
         updateBuildingIconSizeForHover(hoveredFeatureId, false);
         hoveredFeatureId = null;
         if (handlers.onFeatureHover) {
            handlers.onFeatureHover(null);
         }
      }
   };

   if (!map.getLayer(LAYER_ID_POLY)) {
      const layerSpecPoly: any = {
         id: LAYER_ID_POLY,
         type: 'fill',
         source: SOURCE_ID_POLY,
         paint: {
            'fill-color':
               ['case', ['to-boolean', ['feature-state', 'checked']], '#3a8e87', '#606060'],

            'fill-opacity':
               ["interpolate", ["linear"], ["zoom"], 13, 0, 14, .5]


         }
      };
      map.addLayer(layerSpecPoly);
   }

   if (!map.getLayer(LAYER_ID_POLY + "-outline")) {
      const layerSpecPolyOutline: any = {
         id: LAYER_ID_POLY + "-outline",
         type: 'line',
         source: SOURCE_ID_POLY,
         paint: {
            'line-color': '#ffffff',
            'line-opacity': ["interpolate", ["linear"], ["zoom"], 12.5, 0, 13.5, 1],
            'line-width': ['case', ['to-boolean', ['feature-state', 'hover']], 3, 1]
         }
      };
      map.addLayer(layerSpecPolyOutline);
   }

   if (!map.getLayer(LAYER_ID_FARMLAND)) {
      const farmlandLayerSpec: any = {
         id: LAYER_ID_FARMLAND,
         type: 'fill',
         source: SOURCE_ID_FARMLAND,
         paint: {
            'fill-pattern': "mit-patt-char-🚜-1",
            'fill-opacity': ["interpolate", ["linear"], ["zoom"], 13, 0, 14, 0.6]
         }
      };
      map.addLayer(farmlandLayerSpec);
   }

   if (!map.getLayer(LAYER_ID_FARMLAND + "-outline")) {
      const farmlandOutlineLayerSpec: any = {
         id: LAYER_ID_FARMLAND + "-outline",
         type: 'line',
         source: SOURCE_ID_FARMLAND,
         paint: {
            'line-color': '#ffa500',
            'line-opacity': ["interpolate", ["linear"], ["zoom"], 12.5, 0, 13.5, 0.8],
            'line-width': 2
         }
      };
      map.addLayer(farmlandOutlineLayerSpec);
   }

   if (!map.getLayer(LAYER_ID_JORDBUNDSKORT)) {
      const jordbundskortLayerSpec: any = {
         id: LAYER_ID_JORDBUNDSKORT,
         type: 'fill',
         source: SOURCE_ID_JORDBUNDSKORT,
         paint: {
            // Use the color property we set in the GeoJSON, with fallback to case expression
            'fill-color': [
               'coalesce',
               ['get', 'color'],
               [
                  'case',
                  ['==', ['get', 'jordtype'], 'Grovsandet jord'], 'rgb(255, 240, 166)',
                  ['==', ['get', 'jordtype'], 'Finsandet jord'], 'rgb(255, 206, 181)',
                  ['==', ['get', 'jordtype'], 'Grov lerblandet sandjord'], 'rgb(252, 187, 96)',
                  ['==', ['get', 'jordtype'], 'Fin lerblandet sandjord'], 'rgb(250, 168, 15)',
                  ['==', ['get', 'jordtype'], 'Grov sandblandet lerjord'], 'rgb(201, 156, 87)',
                  ['==', ['get', 'jordtype'], 'Fin sandblandet lerjord'], 'rgb(161, 106, 3)',
                  ['==', ['get', 'jordtype'], 'Lerjord'], 'rgb(130, 85, 17)',
                  ['==', ['get', 'jordtype'], 'Svær lerjord'], 'rgb(91, 150, 54)',
                  ['==', ['get', 'jordtype'], 'Meget svær lerjord'], 'rgb(76, 99, 0)',
                  ['==', ['get', 'jordtype'], 'siltjord'], 'rgb(74, 74, 6)',
                  ['==', ['get', 'jordtype'], 'Humusjord'], 'rgb(200, 230, 80)',
                  // Also check for original property names
                  ['==', ['get', 'Jordtype'], 'Grovsandet jord'], 'rgb(255, 240, 166)',
                  ['==', ['get', 'Jordtype'], 'Finsandet jord'], 'rgb(255, 206, 181)',
                  ['==', ['get', 'Jordtype'], 'Grov lerblandet sandjord'], 'rgb(252, 187, 96)',
                  ['==', ['get', 'Jordtype'], 'Fin lerblandet sandjord'], 'rgb(250, 168, 15)',
                  ['==', ['get', 'Jordtype'], 'Grov sandblandet lerjord'], 'rgb(201, 156, 87)',
                  ['==', ['get', 'Jordtype'], 'Fin sandblandet lerjord'], 'rgb(161, 106, 3)',
                  ['==', ['get', 'Jordtype'], 'Lerjord'], 'rgb(130, 85, 17)',
                  ['==', ['get', 'Jordtype'], 'Svær lerjord'], 'rgb(91, 150, 54)',
                  ['==', ['get', 'Jordtype'], 'Meget svær lerjord'], 'rgb(76, 99, 0)',
                  ['==', ['get', 'Jordtype'], 'siltjord'], 'rgb(74, 74, 6)',
                  ['==', ['get', 'Jordtype'], 'Humusjord'], 'rgb(200, 230, 80)',
                  'rgb(128, 128, 128)' // Default gray for unknown types
               ]
            ],
            'fill-opacity': ["interpolate", ["linear"], ["zoom"], 13, 0, 14, 0.4],
            'fill-outline-color': '#00000000',
         }
      };
      map.addLayer(jordbundskortLayerSpec);
   }


   if (!map.getLayer(LAYER_ID)) {
      const layerSpec: any = {
         id: LAYER_ID,
         type: 'circle',
         source: SOURCE_ID,
         paint: {
            'circle-color':
               [
                  'case',
                  ['to-boolean', ['coalesce', ['get', 'buildingLocked'], false]], '#1f1f1f',
                  ['to-boolean', ['feature-state', 'checked']], '#3a8e87',
                  '#606060'
               ],
            'circle-radius':
               ['case', ['to-boolean', ['feature-state', 'hover']], 15, 11],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#fff'
         }
      };
      map.addLayer(layerSpec);
   } else {
      map.setFilter(LAYER_ID, null);
      map.setPaintProperty(LAYER_ID, 'circle-radius',
         ['case', ['to-boolean', ['feature-state', 'hover']], 15, 11]
      );
      map.setPaintProperty(LAYER_ID, 'circle-color', [
         'case',
         ['to-boolean', ['coalesce', ['get', 'buildingLocked'], false]], '#1f1f1f',
         ['to-boolean', ['feature-state', 'checked']], '#3a8e87',
         '#606060'
      ]);
   }

   if (!map.getLayer(LAYER_ID_BUILDING)) {
      const buildingLayerSpec: any = {
         id: LAYER_ID_BUILDING,
         type: 'symbol',
         source: SOURCE_ID,
         filter: ['==', ['get', 'hasBuilding'], true],
         layout: {
            'icon-image': [
               'coalesce',
               ['get', 'buildingIconName'],
               'jpb-house-unchecked'
            ],
            'icon-size': [
               'coalesce',
               ['get', 'buildingIconSize'],
               1.0
            ],
            'icon-anchor': 'center',
            'icon-allow-overlap': true,
            'icon-ignore-placement': true
         },
         paint: {
            'icon-opacity': [
               'case',
               ['to-boolean', ['feature-state', 'hover']], 0.9,
               1
            ]
         }
      };
      map.addLayer(buildingLayerSpec);

      ensureJPBBuildingIcons(map).catch(err => {
         console.error('Failed to pre-load JPB building icons:', err);
      });
   } else {
      map.setFilter(LAYER_ID_BUILDING, ['==', ['get', 'hasBuilding'], true]);
   }

   updateFeatureStates(map, properties, checkedItems);

   map.on("click", LAYER_ID, clickHandler);
   map.on("mousemove", LAYER_ID, mouseMoveHandler);
   map.on("mouseleave", LAYER_ID, mouseLeaveHandler);

   map.on("click", LAYER_ID_BUILDING, clickHandler);
   map.on("mousemove", LAYER_ID_BUILDING, mouseMoveHandler);
   map.on("mouseleave", LAYER_ID_BUILDING, mouseLeaveHandler);

   map.on("click", LAYER_ID_POLY, clickHandler);
   map.on("mousemove", LAYER_ID_POLY, mouseMoveHandler);
   map.on("mouseleave", LAYER_ID_POLY, mouseLeaveHandler);

   setupTextLayers(map, priceMode, areaUnit);

   return () => {
      if (map.getLayer(LAYER_ID)) {
         map.off("click", LAYER_ID, clickHandler);
         map.off("mousemove", LAYER_ID, mouseMoveHandler);
         map.off("mouseleave", LAYER_ID, mouseLeaveHandler);
      }
      if (map.getLayer(LAYER_ID_BUILDING)) {
         map.off("click", LAYER_ID_BUILDING, clickHandler);
         map.off("mousemove", LAYER_ID_BUILDING, mouseMoveHandler);
         map.off("mouseleave", LAYER_ID_BUILDING, mouseLeaveHandler);
      }
      if (map.getLayer(LAYER_ID_POLY)) {
         map.off("click", LAYER_ID_POLY, clickHandler);
         map.off("mousemove", LAYER_ID_POLY, mouseMoveHandler);
         map.off("mouseleave", LAYER_ID_POLY, mouseLeaveHandler);
      }
      map.getCanvas().style.cursor = '';

      if (map.getLayer(ORIGINAL_TEXT_LAYER_ID)) {
         map.removeLayer(ORIGINAL_TEXT_LAYER_ID);
      }
      if (map.getLayer(TEXT_LAYER_ID)) {
         map.removeLayer(TEXT_LAYER_ID);
      }
      if (map.getLayer(LAYER_ID_BUILDING)) {
         map.removeLayer(LAYER_ID_BUILDING);
      }
      if (map.getLayer(LAYER_ID_POLY + "-outline")) {
         map.removeLayer(LAYER_ID_POLY + "-outline");
      }
      if (map.getLayer(LAYER_ID_JORDBUNDSKORT + "-outline")) {
         map.removeLayer(LAYER_ID_JORDBUNDSKORT + "-outline");
      }
      if (map.getLayer(LAYER_ID_JORDBUNDSKORT)) {
         map.removeLayer(LAYER_ID_JORDBUNDSKORT);
      }
      if (map.getLayer(LAYER_ID_FARMLAND + "-outline")) {
         map.removeLayer(LAYER_ID_FARMLAND + "-outline");
      }
      if (map.getLayer(LAYER_ID_FARMLAND)) {
         map.removeLayer(LAYER_ID_FARMLAND);
      }
      if (map.getLayer(LAYER_ID_POLY)) {
         map.removeLayer(LAYER_ID_POLY);
      }
      if (map.getLayer(LAYER_ID)) {
         map.removeLayer(LAYER_ID);
      }

      if (map.getSource(SOURCE_ID_JORDBUNDSKORT)) {
         map.removeSource(SOURCE_ID_JORDBUNDSKORT);
      }
      if (map.getSource(SOURCE_ID_FARMLAND)) {
         map.removeSource(SOURCE_ID_FARMLAND);
      }
      if (map.getSource(SOURCE_ID_POLY)) {
         map.removeSource(SOURCE_ID_POLY);
      }
      if (map.getSource(SOURCE_ID)) {
         map.removeSource(SOURCE_ID);
      }
   };
}


function setupTextLayers(map: MaplibreMap, priceMode: PriceMode, areaUnit: AreaUnit) {
   let formattedPriceProperty: string;
   let textFilter: any = null;
   const isHectare = areaUnit === AreaUnit.Hectare;
   const unitLabel = isHectare ? ' kr/ha' : ' kr/m²';

   if (priceMode === PriceMode.Landbrug) {
      formattedPriceProperty = isHectare ? 'formattedLandbrugHa' : 'formattedLandbrugM2';
      textFilter = ['all', ['has', 'effectiveMarkPrice'], ['>', ['get', 'effectiveMarkPrice'], 0]];
   } else {
      formattedPriceProperty = isHectare ? 'formattedStandardHa' : 'formattedStandardM2';
   }

   const textFieldExpression = [
      'concat',
      ['get', formattedPriceProperty],
      unitLabel
   ];

   if (map.getLayer(TEXT_LAYER_ID)) {
      map.removeLayer(TEXT_LAYER_ID);
   }

   const textLayerSpec: any = {
      id: TEXT_LAYER_ID,
      type: 'symbol',
      source: SOURCE_ID,
      layout: {
         'text-field': textFieldExpression,
         'text-font': ['Noto Sans Bold'],
         'text-size': 16,
         'text-anchor': 'top',
         'text-offset': [0, 0.7],
         'text-allow-overlap': true,
         'text-ignore-placement': true
      },
      paint: {
         'text-color': 'white',
         'text-halo-color': 'black',
         'text-halo-width': 2,
         'text-opacity': ["step", ["zoom"], 0, 11.4, ["case", ["coalesce", ["feature-state", "checked"], false], 1, 0]]
      }
   };

   if (textFilter !== null) {
      textLayerSpec.filter = textFilter;
   }

   map.addLayer(textLayerSpec);
   map.setLayerZoomRange(TEXT_LAYER_ID, 0, 24);

   const originalFormattedPriceProperty = isHectare ? 'formattedOriginalHa' : 'formattedOriginalM2';
   const originalUnitLabel = isHectare ? ' kr/ha' : ' kr/m²';

   if (!map.getLayer(ORIGINAL_TEXT_LAYER_ID)) {
      const originalTextLayerSpec: any = {
         id: ORIGINAL_TEXT_LAYER_ID,
         type: 'symbol',
         source: SOURCE_ID,
         layout: {
            'text-field': [
               'concat',
               ['get', originalFormattedPriceProperty],
               originalUnitLabel,
               ' (',
               ['get', 'transactionDateFormatted'],
               ')'
            ],
            'text-font': ['Noto Sans Regular'],
            'text-size': 14,
            'text-anchor': 'top',
            'text-offset': [0, 2.6],
            'text-allow-overlap': true,
            'text-ignore-placement': true
         },
         paint: {
            'text-color': 'white',
            'text-halo-color': 'black',
            'text-halo-width': 2,
            'text-opacity': ["step", ["zoom"], 0, 11.4, ["case", ["all", ["coalesce", ["feature-state", "hover"], false], ["coalesce", ["feature-state", "checked"], false]], 1, 0]]
         },
      };
      map.addLayer(originalTextLayerSpec);
   } else {
      map.setLayoutProperty(ORIGINAL_TEXT_LAYER_ID, 'text-field', [
         'concat',
         ['get', originalFormattedPriceProperty],
         originalUnitLabel,
         ' (',
         ['get', 'transactionDateFormatted'],
         ')'
      ]);
      map.setLayoutProperty(ORIGINAL_TEXT_LAYER_ID, 'text-allow-overlap', true);
      map.setLayoutProperty(ORIGINAL_TEXT_LAYER_ID, 'text-ignore-placement', true);
      map.setPaintProperty(ORIGINAL_TEXT_LAYER_ID, 'text-opacity', [
         "step",
         ["zoom"],
         0,
         11.4,
         ["case",
            ["coalesce", ["feature-state", "hover"], false],
            1,
            0
         ]
      ]);
      map.setFilter(ORIGINAL_TEXT_LAYER_ID, null);
      const originalTextLayer = map.getLayer(ORIGINAL_TEXT_LAYER_ID);
      if (originalTextLayer) {
         map.setLayerZoomRange(ORIGINAL_TEXT_LAYER_ID, 0, 24);
      }
   }
}

/**
 * Removes all JPB layers and sources from the map
 */
export function removeJPBMapLayers(map: MaplibreMap | null | undefined) {
   if (!map) return;

   if (map.getLayer(TEXT_LAYER_ID)) map.removeLayer(TEXT_LAYER_ID);
   if (map.getLayer(ORIGINAL_TEXT_LAYER_ID)) map.removeLayer(ORIGINAL_TEXT_LAYER_ID);
   if (map.getLayer(LAYER_ID_BUILDING)) map.removeLayer(LAYER_ID_BUILDING);
   if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID);
   if (map.getLayer(LAYER_ID_POLY + "-outline")) map.removeLayer(LAYER_ID_POLY + "-outline");
   if (map.getLayer(LAYER_ID_POLY)) map.removeLayer(LAYER_ID_POLY);
   if (map.getLayer(LAYER_ID_JORDBUNDSKORT + "-outline")) map.removeLayer(LAYER_ID_JORDBUNDSKORT + "-outline");
   if (map.getLayer(LAYER_ID_JORDBUNDSKORT)) map.removeLayer(LAYER_ID_JORDBUNDSKORT);
   if (map.getLayer(LAYER_ID_FARMLAND + "-outline")) map.removeLayer(LAYER_ID_FARMLAND + "-outline");
   if (map.getLayer(LAYER_ID_FARMLAND)) map.removeLayer(LAYER_ID_FARMLAND);
   if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
   if (map.getSource(SOURCE_ID_JORDBUNDSKORT)) map.removeSource(SOURCE_ID_JORDBUNDSKORT);
   if (map.getSource(SOURCE_ID_FARMLAND)) map.removeSource(SOURCE_ID_FARMLAND);
   if (map.getSource(SOURCE_ID_POLY)) map.removeSource(SOURCE_ID_POLY);
}

/**
 * Updates feature states for checked items
 */
export function updateFeatureStates(
   map: MaplibreMap,
   properties: JPBReferenceProperty[],
   checkedItems: Set<string>
) {
   const source = map.getSource(SOURCE_ID) as GeoJSONSource;
   if (source && source.type === 'geojson') {
      const data = (source as any)._data as any;
      if (data && data.type === 'FeatureCollection' && Array.isArray(data.features)) {
         let needsUpdate = false;
         data.features.forEach((feature: any) => {
            if (feature.properties?.hasBuilding && feature.properties?.documentID) {
               const isChecked = checkedItems.has(feature.properties.documentID);
               const buildingLocked = feature.properties.buildingLocked;
               let newIconName: string | null = null;

               if (buildingLocked) {
                  newIconName = 'jpb-house-locked';
               } else if (isChecked) {
                  newIconName = 'jpb-house-checked';
               } else {
                  newIconName = 'jpb-house-unchecked';
               }

               if (feature.properties.buildingIconName !== newIconName) {
                  feature.properties.buildingIconName = newIconName;
                  needsUpdate = true;
               }
            }
         });

         if (needsUpdate) {
            source.setData({ ...data });
         }
      }
   }

   // Update feature states
   properties.forEach(prop => {
      if (prop.documentID) {
         const isChecked = checkedItems.has(prop.documentID);
         map.setFeatureState(
            { source: SOURCE_ID, id: prop.documentID },
            { checked: isChecked, hover: false }
         );
         map.setFeatureState(
            { source: SOURCE_ID_POLY, id: prop.documentID },
            { checked: isChecked, hover: false }
         );
         map.setFeatureState(
            { source: SOURCE_ID_FARMLAND, id: prop.documentID },
            { checked: isChecked, hover: false }
         );

         const jordbundskortGeometry = prop.overlapGeometry?.jordbundskortGeometry;
         if (jordbundskortGeometry?.type === "FeatureCollection" && Array.isArray(jordbundskortGeometry.features)) {
            jordbundskortGeometry.features.forEach((_f: any, index: number) => {
               map.setFeatureState(
                  { source: SOURCE_ID_JORDBUNDSKORT, id: `${prop.documentID}-jordbundskort-${index}` },
                  { checked: isChecked, hover: false }
               );
            });
         }
      }
   });
}


export function highlightPolygonOnMap(
   map: MaplibreMap,
   geometry: any,
   documentID: string,
   timeoutMs: number = 5000
): () => void {
   // Remove any existing highlight first
   if (map.getLayer(LAYER_ID_HIGHLIGHT)) {
      map.removeLayer(LAYER_ID_HIGHLIGHT);
   }
   if (map.getSource(SOURCE_ID_HIGHLIGHT)) {
      map.removeSource(SOURCE_ID_HIGHLIGHT);
   }

   // Create GeoJSON feature from geometry
   const highlightFeature = {
      type: "Feature" as const,
      id: documentID,
      geometry: geometry,
      properties: {
         documentID: documentID
      }
   };

   // Add source
   map.addSource(SOURCE_ID_HIGHLIGHT, {
      type: 'geojson',
      data: {
         type: "FeatureCollection" as const,
         features: [highlightFeature]
      }
   });

   map.addLayer({
      id: LAYER_ID_HIGHLIGHT,
      type: 'line',
      source: SOURCE_ID_HIGHLIGHT,
      paint: {
         'line-color': '#22c55e', // More muted green 
         'line-width': 5,
         'line-opacity': 1
      },
      layout: {
         'line-join': 'round',
         'line-cap': 'round'
      }
   });

   // Set up auto-removal
   const timeoutId = setTimeout(() => {
      if (map.getLayer(LAYER_ID_HIGHLIGHT)) {
         map.removeLayer(LAYER_ID_HIGHLIGHT);
      }
      if (map.getSource(SOURCE_ID_HIGHLIGHT)) {
         map.removeSource(SOURCE_ID_HIGHLIGHT);
      }
   }, timeoutMs);

   // Return cleanup function
   return () => {
      clearTimeout(timeoutId);
      if (map.getLayer(LAYER_ID_HIGHLIGHT)) {
         map.removeLayer(LAYER_ID_HIGHLIGHT);
      }
      if (map.getSource(SOURCE_ID_HIGHLIGHT)) {
         map.removeSource(SOURCE_ID_HIGHLIGHT);
      }
   };
}

